import React, { useState, useEffect } from 'react';
import ReactQuill from 'react-quill';
import { Button, Modal, Space, Tooltip } from 'antd';
import { FullscreenOutlined, EditOutlined, EyeOutlined, CloseOutlined } from '@ant-design/icons';
import 'react-quill/dist/quill.snow.css';
import './index.scss';
const fontSizes = ['8px','9px','10px','12px','14px','16px','20px','24px','32px','42px','54px','68px','84px','98px'];
const Size = ReactQuill.Quill.import('attributors/style/size');
Size.whitelist = fontSizes;

ReactQuill.Quill.register(Size, true);


const modules = {
  toolbar: [
    [{ header: [1, 2, 3, false] }],
    [{ size: fontSizes }],
    ['bold', 'italic', 'underline', 'strike'],
    ['blockquote'],
    [{ list: 'ordered' }, { list: 'bullet' }],
    [{ script: 'sub' }, { script: 'super' }],
    [{ indent: '-1' }, { indent: '+1' }],
    [{ direction: 'rtl' }],
    [{ color: [] }, { background: [] }],
    [{ align: [] }],
    ['link', 'image'],
  ],
};

const formats = [
  'header',
  'size',
  'font',
  'bold',
  'italic',
  'underline',
  'strike',
  'blockquote',
  'list',
  'bullet',
  'indent',
  'link',
  'image',
  'color',
  'background',
  'align',
  'script',
  'direction',
];

const AppEditor = ({
  value,
  onChange,
  placeholder = 'Enter content...',
  readOnly = false,
  disabled = false,
  theme = 'snow',
  style = {
    height: '200px',
  },
  showFullscreen = true,
  ...props
}) => {
  const [isFullscreenOpen, setIsFullscreenOpen] = useState(false);
  const [fullscreenValue, setFullscreenValue] = useState('');

  // Sync fullscreen value with prop value when modal opens
  useEffect(() => {
    if (isFullscreenOpen) {
      // Use setTimeout to ensure ReactQuill is properly mounted
      setTimeout(() => {
        setFullscreenValue(value || '');
      }, 100);
    }
  }, [isFullscreenOpen, value]);

  const handleFullscreen = () => {
    setFullscreenValue(value || '');
    setIsFullscreenOpen(true);
  };

  const handleFullscreenSave = () => {
    if (onChange) {
      onChange(fullscreenValue);
    }
    setIsFullscreenOpen(false);
  };

  const handleFullscreenCancel = () => {
    setFullscreenValue(value || '');
    setIsFullscreenOpen(false);
  };

  const ActionButtons = ({ className = '' }) => (
    <Space className={className}>
      {showFullscreen && (
        <Tooltip title="Fullscreen">
          <Button
            icon={<FullscreenOutlined />}
            size="small"
            onClick={handleFullscreen}
            type="text"
            disabled={disabled}
          />
        </Tooltip>
      )}
    </Space>
  );

  return (
    <>
      <div style={style} className="relative app-editor-wrapper">
        <ReactQuill
          className="app-editor-container"
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          readOnly={readOnly || disabled}
          theme={theme}
          modules={modules}
          formats={formats}
          {...props}
        />

        <ActionButtons className="absolute -top-[30px] right-2 z-10" />
      </div>

      {/* Fullscreen Editor Modal */}
      <Modal
        title={
          <Space className="w-full justify-between items-center">
            <span>Edit</span>

            <Button
              type="text"
              icon={<CloseOutlined />}
              onClick={() => setIsFullscreenOpen(false)}
              size="small"
            />
          </Space>
        }
        closeIcon={null}
        open={isFullscreenOpen}
        onCancel={handleFullscreenCancel}
        footer={[
          <Button key="cancel" onClick={handleFullscreenCancel}>
            Cancel
          </Button>,
          <Button key="save" type="primary" onClick={handleFullscreenSave}>
            Save
          </Button>,
        ]}
        width="90vw"
        style={{ top: 20 }}
        className="editor-fullscreen-modal"
        destroyOnClose={false}
        forceRender={true}
      >
        <div style={{ height: '100%' }}>
          <ReactQuill
            className="app-editor-container"
            value={fullscreenValue}
            onChange={setFullscreenValue}
            placeholder={placeholder}
            theme={theme}
            modules={modules}
            formats={formats}
            style={{ height: 'calc(100% - 42px)' }}
            key={isFullscreenOpen ? 'fullscreen-editor' : 'editor-closed'}
          />
        </div>
      </Modal>
    </>
  );
};

export default AppEditor;
